import { useContext, useState, useEffect } from 'react'
import { FormikContext, getIn } from 'formik'
import { ButtonDrawer } from '~/components'
import * as inputStyles from '~/components/fields/InputField/index.module.css'
import { useTranslation } from 'react-i18next'

export default function EditField({ fields, title, onClose, validate, formValues: passedValues, onFieldSave }) {
  const { t } = useTranslation()
  const formik = useContext(FormikContext)
  const values = passedValues ?? formik?.values ?? {}
  const setFieldValue = onFieldSave
    ? (id, val) => onFieldSave(id, val)
    : (id, val, shouldValidate) => formik?.setFieldValue(id, val, shouldValidate)
  const [initialized, setInitialized] = useState(false)

  // Local state to hold temporary values
  const [localValues, setLocalValues] = useState({})
  const [localErrors, setLocalErrors] = useState({})

  // Initialize local state with current form values when modal opens
  // Only initialize once to avoid resetting while user is typing
  // DO NOT include 'values' in dependency array - it would cause re-initialization during polling
  useEffect(() => {
    if (!initialized && fields) {
      const initialValues = {}
      fields?.forEach(field => {
        initialValues[field.id] = getIn(values, field.id) || ''
      })
      setLocalValues(initialValues)
      setLocalErrors({})
      setInitialized(true)
    }
  }, [fields, initialized]) // Removed 'values' from dependencies to prevent re-init during polling

  // Reset initialized flag only when component unmounts
  // Don't reset when fields changes - that would cause re-init during parent re-renders
  useEffect(() => {
    return () => {
      setInitialized(false)
    }
  }, []) // Empty dependency array - cleanup only on unmount

  const handleChange = (fieldId, value) => {
    setLocalValues(prev => ({
      ...prev,
      [fieldId]: value
    }))

    // Validate if needed
    const field = fields.find(f => f.id === fieldId)
    if (validate && field?.validate) {
      const error = field.validate(value)
      setLocalErrors(prev => ({
        ...prev,
        [fieldId]: error || null
      }))
    }
  }

  const onSubmit = () => {
    // Validate all fields if validation is enabled
    if (validate) {
      const newErrors = {}
      let hasErrors = false

      fields?.forEach(field => {
        if (field.validate) {
          const error = field.validate(localValues[field.id] || '')
          if (error) {
            newErrors[field.id] = error
            hasErrors = true
          }
        }
      })

      setLocalErrors(newErrors)

      if (hasErrors) {
        return // Don't close if there are errors
      }
    }

    // Save all field values to the form
    fields?.forEach(field => {
      setFieldValue(field.id, localValues[field.id] || '')
    })

    setInitialized(false) // Reset for next time modal opens
    onClose()
  }

  return (
    <>
      <div className="flex flex-col w-full p-4 gap-1.5">
        <p className="text-[17px] font-semibold uppercase text-[#17181a] mb-2.5">{title}</p>
        {fields?.map(field => {
          return (
            <div key={field.id}>
              <input
                className={inputStyles.field}
                name={field.id}
                value={localValues[field.id] || ''}
                onChange={(e) => handleChange(field.id, e.target.value)}
                onBlur={() => {}}
                placeholder={field.title}
                tabIndex="0"
                maxLength={40}
              />
              {localErrors[field.id] && (
                <p className="text-red-600 text-[12px] mt-1">{localErrors[field.id]}</p>
              )}
            </div>
          )
        })}
      </div>
      <ButtonDrawer buttons={[{ func: onSubmit, title: t('button-save')}]} modal />
    </>
  )
}
